import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { findLastIndex, sortBy } from '@sorare/core/src/lib/arrays';
import { withFragments } from '@sorare/core/src/lib/gql';
import { isActiveAppearance } from '@sorare/core/src/lib/players';

import type {
  getContenderLeftFooterState_so5LeaderboardContender,
  getContenderRightFooterState_so5LeaderboardContender,
  getContenderStatus_so5LeaderboardContender,
  getNextEligibleRewardsState_so5LeaderboardContender,
  getSecondaryContenderFooterState_so5LeaderboardContender,
  hasContenderStreakReward_so5LeaderboardContender,
} from './__generated__/contender.graphql.ts';
import { hasGamesStarted, isFixtureOpened } from './so5.ts';

export enum ContenderStatus {
  EMPTY = 'EMPTY',
  PROJECTED_DIVISION = 'PROJECTED_DIVISION',
  INCOMPLETE = 'INCOMPLETE',
  VIEW_BONUS = 'VIEW_BONUS',
  BONUS = 'BONUS',
}

const THRESHOLDS_COLORS = ['51, 206, 120', '112, 219, 198', '194, 219, 46'];

type ContenderStatusReturnType =
  | {
      status: ContenderStatus.PROJECTED_DIVISION;
      divisionChange: DivisionChange;
      projectedDivision: number;
    }
  | { status: ContenderStatus.EMPTY }
  | { status: ContenderStatus.INCOMPLETE }
  | { status: ContenderStatus.VIEW_BONUS }
  | { status: ContenderStatus.BONUS };

export const getContenderStatus = withFragments(
  (
    contender: getContenderStatus_so5LeaderboardContender
  ): ContenderStatusReturnType => {
    const { managerTeam, so5Leaderboard, so5Lineup } = contender;
    const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);

    const projectedLineup = managerTeam?.divisionChangingSo5Lineup;
    const projectedRanking = projectedLineup?.so5Rankings[0];

    if (
      isUpcoming &&
      so5Lineup &&
      (!so5Lineup.confirmable || so5Lineup.draft)
    ) {
      return { status: ContenderStatus.INCOMPLETE };
    }
    if (isUpcoming && !so5Lineup && projectedRanking?.eligibleDivisionChange) {
      const projectedDivisionDelta =
        projectedRanking.eligibleDivisionChange === DivisionChange.PROMOTED
          ? -1
          : +1;
      const projectedDivision =
        (so5Leaderboard.division || 0) +
        (projectedRanking.eligibleDivisionChange === DivisionChange.STABLE
          ? 0
          : projectedDivisionDelta);

      return {
        status: ContenderStatus.PROJECTED_DIVISION,
        divisionChange: projectedRanking?.eligibleDivisionChange,
        projectedDivision,
      };
    }
    if (isUpcoming && !so5Lineup) {
      return { status: ContenderStatus.EMPTY };
    }
    if (isUpcoming && so5Lineup) {
      return { status: ContenderStatus.VIEW_BONUS };
    }

    return { status: ContenderStatus.BONUS };
  },
  {
    so5LeaderboardContender: gql`
      fragment getContenderStatus_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          division
          so5Fixture {
            slug
            ...isFixtureOpened_so5Fixture
          }
        }
        so5Lineup {
          id
          draft
          confirmable
        }
        managerTeam {
          id
          divisionChangingSo5Lineup {
            id
            so5Rankings {
              id
              eligibleDivisionChange
            }
          }
        }
      }
      ${isFixtureOpened.fragments.so5Fixture}
    ` as TypedDocumentNode<getContenderStatus_so5LeaderboardContender>,
  }
);

export enum ContenderRightFooterState {
  LEADERBOARD = 'LEADERBOARD',
  DIVISION_CHANGE = 'DIVISION_CHANGE',
  TARGET_IN_PROGRESS = 'TARGET_IN_PROGRESS',
  TARGET_MISSED = 'TARGET_MISSED',
  TARGET_SUCCESS = 'TARGET_SUCCESS',
  THRESHOLD_IN_PROGRESS = 'THRESHOLD_IN_PROGRESS',
  THRESHOLD_MISSED = 'THRESHOLD_MISSED',
  THRESHOLD_SUCCESS = 'THRESHOLD_SUCCESS',
  POINTS_TO_NEXT_THRESHOLD = 'POINTS_TO_NEXT_THRESHOLD',
}

type ContenderRightFooterStateReturnType =
  | {
      state: ContenderRightFooterState.DIVISION_CHANGE;
      divisionChange: DivisionChange;
    }
  | { state: ContenderRightFooterState.TARGET_SUCCESS; iconUrl: string }
  | { state: ContenderRightFooterState.TARGET_IN_PROGRESS; iconUrl: string }
  | { state: ContenderRightFooterState.TARGET_MISSED }
  | {
      state: ContenderRightFooterState.THRESHOLD_IN_PROGRESS;
    }
  | {
      state: ContenderRightFooterState.POINTS_TO_NEXT_THRESHOLD;
      thresholdNumber: number;
      color: string;
      points: number;
    }
  | {
      state: ContenderRightFooterState.THRESHOLD_SUCCESS;
      thresholdNumber: number;
      color: string;
    }
  | {
      state: ContenderRightFooterState.THRESHOLD_MISSED;
    }
  | {
      state: ContenderRightFooterState.LEADERBOARD;
    }
  | null;

export const getContenderRightFooterState = withFragments(
  (
    contender: getContenderRightFooterState_so5LeaderboardContender
  ): ContenderRightFooterStateReturnType => {
    const { so5Leaderboard, so5Lineup } = contender;

    const so5Ranking = so5Lineup?.so5Rankings.at(0);
    const stillHasGamesToPlay = so5Lineup?.so5Appearances
      .filter(isActiveAppearance)
      .some(so5Appearance => !!so5Appearance.upcomingGame);

    const thresholds = sortBy(
      ({ score }) => score || 0,
      (so5Leaderboard.rewardsConfig.conditional || []).filter(Boolean)
    );
    const higherTresholdReachedIndex = findLastIndex(
      thresholds,
      so5RewardConfig => {
        if (!so5RewardConfig?.score || !so5Ranking?.score) return false;
        return so5RewardConfig.score <= so5Ranking.score;
      }
    );

    if (!so5Lineup || so5Lineup?.cancelledAt) {
      return null;
    }
    if (so5Ranking?.eligibleDivisionChange) {
      return {
        state: ContenderRightFooterState.DIVISION_CHANGE,
        divisionChange: so5Ranking.eligibleDivisionChange,
      };
    }
    if (so5Ranking?.targetedRewardWithEligibility?.reward.target) {
      if (so5Ranking.targetedRewardWithEligibility.eligible) {
        return {
          state: ContenderRightFooterState.TARGET_SUCCESS,
          iconUrl:
            so5Ranking.targetedRewardWithEligibility.reward.target.pictureUrl,
        };
      }
      if (stillHasGamesToPlay) {
        return {
          state: ContenderRightFooterState.TARGET_IN_PROGRESS,
          iconUrl:
            so5Ranking.targetedRewardWithEligibility.reward.target.pictureUrl,
        };
      }
      return { state: ContenderRightFooterState.TARGET_MISSED };
    }
    if (thresholds.length > 0) {
      if (stillHasGamesToPlay && higherTresholdReachedIndex === -1) {
        return {
          state: ContenderRightFooterState.THRESHOLD_IN_PROGRESS,
        };
      }
      if (
        higherTresholdReachedIndex >= 0 &&
        higherTresholdReachedIndex < thresholds.length - 1
      ) {
        return {
          state: ContenderRightFooterState.POINTS_TO_NEXT_THRESHOLD,
          points: Math.ceil(
            (thresholds.at(higherTresholdReachedIndex + 1)?.score || 0) -
              (so5Ranking?.score || 0)
          ),
          thresholdNumber: higherTresholdReachedIndex + 2,
          color: THRESHOLDS_COLORS[higherTresholdReachedIndex],
        };
      }
      if (higherTresholdReachedIndex >= 0) {
        return {
          state: ContenderRightFooterState.THRESHOLD_SUCCESS,
          thresholdNumber: higherTresholdReachedIndex + 1,
          color: THRESHOLDS_COLORS[higherTresholdReachedIndex],
        };
      }
      return {
        state: ContenderRightFooterState.THRESHOLD_MISSED,
      };
    }

    return { state: ContenderRightFooterState.LEADERBOARD };
  },
  {
    so5LeaderboardContender: gql`
      fragment getContenderRightFooterState_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          rewardsConfig {
            conditional {
              id
              score
            }
          }
        }
        so5Lineup {
          id
          cancelledAt
          so5Appearances(includeSubs: true) {
            substitutionState
            id
            upcomingGame {
              id
            }
          }
          so5Rankings {
            id
            score
            eligibleDivisionChange
            targetedRewardWithEligibility {
              eligible
              reward {
                id
                target {
                  slug
                  pictureUrl
                }
              }
            }
          }
        }
      }
    ` as TypedDocumentNode<getContenderRightFooterState_so5LeaderboardContender>,
  }
);

export enum ContenderLeftFooterState {
  REWARDS = 'REWARDS',
  NOT_STARTED = 'NOT_STARTED',
  POINTS_TO_CLOSEST_REWARD = 'POINTS_TO_CLOSEST_REWARD',
  POINTS_TO_SAFETY = 'POINTS_TO_SAFETY',
  CANCELLED = 'CANCELLED',
  POINTS_TO_TARGET = 'POINTS_TO_TARGET',
  POINTS_TO_THRESHOLD = 'POINTS_TO_THRESHOLD',
  POINTS_OVER_TARGET = 'POINTS_OVER_TARGET',
}

export type ContenderLeftFooterStateReturnType =
  | { state: ContenderLeftFooterState.CANCELLED }
  | { state: ContenderLeftFooterState.REWARDS }
  | { state: ContenderLeftFooterState.NOT_STARTED; date: Date }
  | { state: ContenderLeftFooterState.POINTS_TO_SAFETY; points: number }
  | {
      state: ContenderLeftFooterState.POINTS_TO_TARGET;
      points: number;
      iconUrl: string;
    }
  | {
      state: ContenderLeftFooterState.POINTS_OVER_TARGET;
      points: number;
    }
  | {
      state: ContenderLeftFooterState.POINTS_TO_THRESHOLD;
      points: number;
      color: string;
      thresholdNumber: number;
    }
  | { state: ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD; points: number }
  | null;

const getNextEligibleRewardsState = withFragments(
  (
    contender: getNextEligibleRewardsState_so5LeaderboardContender
  ): ContenderLeftFooterStateReturnType => {
    const { so5Leaderboard, so5Lineup } = contender;

    const so5Ranking = so5Lineup?.so5Rankings.at(0);

    if (!so5Ranking?.nextEligibleRewards) {
      return null;
    }

    const distance = Math.ceil(
      so5Ranking.nextEligibleRewards.score - so5Ranking.score
    );

    const nextTargetReward =
      so5Ranking?.nextEligibleRewards.eligibleRewards.find(
        so5RewardConfig => so5RewardConfig.target
      );
    if (nextTargetReward) {
      return {
        state: ContenderLeftFooterState.POINTS_TO_TARGET,
        points: distance,
        iconUrl: nextTargetReward.target!.pictureUrl,
      };
    }

    const nextScoreThreshold =
      so5Ranking?.nextEligibleRewards.eligibleRewards.find(
        so5RewardConfig => so5RewardConfig.score
      );
    if (nextScoreThreshold) {
      const thresholds = sortBy(
        ({ score }) => score || 0,
        (so5Leaderboard.rewardsConfig.conditional || []).filter(Boolean)
      );

      const nextThresholdIndex = (thresholds || []).findIndex(
        t => (t?.score ?? 0) === nextScoreThreshold?.score
      );
      return {
        state: ContenderLeftFooterState.POINTS_TO_THRESHOLD,
        points: distance,
        thresholdNumber: nextThresholdIndex + 1,
        color: THRESHOLDS_COLORS[nextThresholdIndex],
      };
    }

    return {
      state: ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD,
      points: distance,
    };
  },
  {
    so5LeaderboardContender: gql`
      fragment getNextEligibleRewardsState_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          rewardsConfig {
            conditional {
              id
              score
            }
          }
        }
        so5Lineup {
          id
          cancelledAt
          so5Rankings {
            id
            score
            nextEligibleRewards {
              score
              eligibleRewards {
                id
                score
                target {
                  slug
                  pictureUrl
                }
              }
            }
          }
        }
      }
    ` as TypedDocumentNode<getNextEligibleRewardsState_so5LeaderboardContender>,
  }
);

const hasContenderStreakReward = withFragments(
  (contender: hasContenderStreakReward_so5LeaderboardContender): boolean => {
    const { so5Lineup } = contender;

    return so5Lineup?.thresholdsStreakTask?.completedBy?.id === so5Lineup?.id;
  },
  {
    so5LeaderboardContender: gql`
      fragment hasContenderStreakReward_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Lineup {
          id
          thresholdsStreakTask {
            id
            completedBy {
              id
            }
          }
        }
      }
    ` as TypedDocumentNode<hasContenderStreakReward_so5LeaderboardContender>,
  }
);

export const getContenderLeftFooterState = withFragments(
  (
    contender: getContenderLeftFooterState_so5LeaderboardContender
  ): ContenderLeftFooterStateReturnType => {
    const { so5Leaderboard, so5Lineup } = contender;

    if (!so5Lineup) {
      return null;
    }
    if (so5Lineup.cancelledAt) {
      return { state: ContenderLeftFooterState.CANCELLED };
    }

    const so5Ranking = so5Lineup.so5Rankings.at(0);
    if (
      so5Ranking?.eligibleOrSo5Rewards.length ||
      hasContenderStreakReward(contender)
    ) {
      return { state: ContenderLeftFooterState.REWARDS };
    }

    const hasLineupGamesStarted = hasGamesStarted(so5Lineup);
    if (!hasLineupGamesStarted && so5Lineup.anyEarliestGame) {
      return {
        state: ContenderLeftFooterState.NOT_STARTED,
        date: so5Lineup.anyEarliestGame.date,
      };
    }

    if (
      so5Ranking?.eligibleDivisionChange === DivisionChange.RELEGATED &&
      so5Leaderboard.divisionChangeConfiguration?.lastStableSo5Ranking
    ) {
      return {
        state: ContenderLeftFooterState.POINTS_TO_SAFETY,
        points: Math.ceil(
          so5Leaderboard.divisionChangeConfiguration.lastStableSo5Ranking
            .score - so5Ranking.score
        ),
      };
    }

    return getNextEligibleRewardsState(contender);
  },
  {
    so5LeaderboardContender: gql`
      fragment getContenderLeftFooterState_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          rewardsConfig {
            conditional {
              id
              score
            }
          }
          divisionChangeConfiguration {
            id
            lastStableSo5Ranking {
              id
              score
            }
          }
        }
        so5Lineup {
          id
          cancelledAt
          anyEarliestGame {
            id
            date
          }
          so5Rankings {
            id
            score
            eligibleDivisionChange
            eligibleOrSo5Rewards {
              ... on So5RewardConfig {
                id
                ranks
              }
              ... on So5Reward {
                slug
              }
            }
          }
          ...hasGamesStarted_so5Lineup
        }
        ...getNextEligibleRewardsState_so5LeaderboardContender
        ...hasContenderStreakReward_so5LeaderboardContender
      }
      ${getNextEligibleRewardsState.fragments.so5LeaderboardContender}
      ${hasGamesStarted.fragments.so5Lineup}
      ${hasContenderStreakReward.fragments.so5LeaderboardContender}
    ` as TypedDocumentNode<getContenderLeftFooterState_so5LeaderboardContender>,
  }
);

export const getSecondaryContenderFooterState = withFragments(
  (
    contender: getSecondaryContenderFooterState_so5LeaderboardContender
  ): ContenderLeftFooterStateReturnType => {
    const so5Ranking = contender.so5Lineup?.so5Rankings.at(0);
    if (!so5Ranking?.eligibleOrSo5Rewards.length) {
      return null;
    }

    const nextEligibleRewardsState = getNextEligibleRewardsState(contender);
    const state = nextEligibleRewardsState?.state;

    if (
      state &&
      ![
        ContenderLeftFooterState.POINTS_TO_TARGET,
        ContenderLeftFooterState.POINTS_TO_THRESHOLD,
      ].includes(state)
    ) {
      return null;
    }

    return nextEligibleRewardsState;
  },
  {
    so5LeaderboardContender: gql`
      fragment getSecondaryContenderFooterState_so5LeaderboardContender on So5LeaderboardContender {
        slug
        so5Lineup {
          id
          so5Rankings {
            id
            eligibleOrSo5Rewards {
              ... on So5RewardConfig {
                id
                ranks
              }
              ... on So5Reward {
                slug
              }
            }
          }
        }
        ...getNextEligibleRewardsState_so5LeaderboardContender
      }
      ${getNextEligibleRewardsState.fragments.so5LeaderboardContender}
    ` as TypedDocumentNode<getSecondaryContenderFooterState_so5LeaderboardContender>,
  }
);
